import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, getErrorMessage } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import AuthShell from "./AuthShell";
import { toast } from "sonner";
import { Loader2, XCircle } from "lucide-react";

export default function AcceptInvitation() {
  const { token } = useParams();
  const nav = useNavigate();
  const { acceptInvitation } = useAuth();
  const [info, setInfo] = useState(null);
  const [loadingInfo, setLoadingInfo] = useState(true);
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api
      .get(`/auth/invitation/${token}`)
      .then((r) => setInfo(r.data))
      .catch(() => setInfo({ valid: false }))
      .finally(() => setLoadingInfo(false));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 8) {
      toast.error("Mot de passe : min. 8 caractères.");
      return;
    }
    setSubmitting(true);
    try {
      await acceptInvitation(token, password, name);
      toast.success("Invitation acceptée. Bienvenue !");
      nav("/");
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  if (loadingInfo) {
    return (
      <AuthShell title="Invitation">
        <div className="flex items-center gap-3 text-zinc-600">
          <Loader2 className="animate-spin" size={18} />
          <span className="text-sm">Chargement…</span>
        </div>
      </AuthShell>
    );
  }

  if (!info?.valid) {
    return (
      <AuthShell title="Invitation">
        <div className="border border-[#DC2626]/30 bg-[#DC2626]/5 p-6" data-testid="invitation-invalid">
          <div className="flex items-start gap-3">
            <XCircle className="text-[#DC2626] shrink-0" size={22} strokeWidth={1.5} />
            <div>
              <div className="text-sm font-medium text-zinc-900">Invitation invalide</div>
              <div className="mt-1 text-sm text-zinc-600">
                Le lien est expiré ou a déjà été utilisé. Contactez l'administrateur.
              </div>
            </div>
          </div>
        </div>
        <Link to="/login" className="inline-block mt-4 text-sm text-[#002FA7] hover:underline">
          ← Connexion
        </Link>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      title="Acceptation d'invitation"
      subtitle={`Compte ${info.role === "admin" ? "administrateur" : "utilisateur"} pour ${info.email}`}
    >
      <form onSubmit={submit} className="space-y-5" data-testid="accept-invitation-form">
        <Field label="E-mail">
          <input
            value={info.email}
            disabled
            className="w-full bg-zinc-50 border border-zinc-200 px-4 py-2.5 text-sm text-zinc-500"
          />
        </Field>
        <Field label="Votre nom">
          <input
            data-testid="invite-name"
            required
            maxLength={120}
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-full bg-white border border-zinc-300 px-4 py-2.5 text-sm focus:border-[#002FA7] focus:ring-1 focus:ring-[#002FA7] outline-none"
          />
        </Field>
        <Field label="Mot de passe (min. 8 caractères)">
          <input
            data-testid="invite-password"
            type="password"
            required
            minLength={8}
            maxLength={128}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full bg-white border border-zinc-300 px-4 py-2.5 text-sm focus:border-[#002FA7] focus:ring-1 focus:ring-[#002FA7] outline-none"
          />
        </Field>
        <button
          data-testid="invite-submit"
          disabled={submitting}
          className="w-full bg-[#002FA7] hover:bg-[#00227A] text-white text-sm font-medium py-3 transition-colors disabled:opacity-50"
        >
          {submitting ? "Validation..." : "Accepter l'invitation"}
        </button>
      </form>
    </AuthShell>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-[11px] uppercase tracking-[0.15em] text-zinc-500 font-mono mb-2">
        {label}
      </span>
      {children}
    </label>
  );
}
